import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatIST } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { validateField } from '@/lib/validators';
import { useAuth } from '@/contexts/AuthContext';
import { PRIVILEGED_ROLES, canManageAccount } from '@/lib/constants';
import FieldError from '@/components/ui/field-error';
import {
  Plus, Search, UserCog, ToggleLeft, ToggleRight, KeyRound, Eye, EyeOff,
  Pencil, RefreshCw, AlertTriangle, Check, Wand2, Trash2,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton, SidePane } from '@/components/data';
import PhoneUsersList from './Users/PhoneUsersList';
import PhoneUserForm from './Users/PhoneUserForm';
import { ROLE_OPTIONS, ROLE_LABEL, randomPassword } from './Users/roles';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Everyone with a login, and what they can reach.
 *
 * Create and Edit were two dialogs with the same six fields, the same
 * secondary-roles block and the same stage picker, kept in two pieces of state
 * that had already drifted — the create form validated a `full_name` it never
 * held. They are one side pane over one form now.
 *
 * The username-change confirmation was a third dialog stacked on the second.
 * It is a warning inside the pane instead: you are told what will happen while
 * you can still see the field you changed.
 *
 * Linked stages become a column. They were collected in the create form and
 * then never shown anywhere, so who covers which stage could only be discovered
 * by opening every supervisor in turn.
 *
 * Product types are the second half of a supervisor's scope, and stages alone
 * could never carry it: Packing and Dispatch are on every pipeline the plant
 * runs, so the man who loads the offset vans is linked to exactly the stages the
 * man who loads the corrugated ones is. Picking none means every type, which is
 * what every account made before this holds.
 */

const EMPTY = {
  full_name: '', username: '', password: '', role: '',
  secondary_roles: [], mobile_number: '', email: '', linked_stage_ids: [],
  linked_product_types: [],
};

const VALIDATORS = {
  full_name: [{ name: 'required', options: { message: 'Full name is required' } }],
  username: [{ name: 'username' }],
  password: [{ name: 'password', options: { minLength: 4 } }],
  role: [{ name: 'required', options: { message: 'Please select a role' } }],
  email: [{ name: 'email' }],
};

export default function UsersPage() {
  const { user: actor } = useAuth();
  const isMobile = useIsMobile();
  // Only an admin may hand out a privileged role or touch an account holding
  // one — the API refuses either way, so the page does not offer it.
  const isAdmin = (actor?.roles?.length ? actor.roles : [actor?.role]).includes('admin');
  const roleOptions = useMemo(
    () => (isAdmin ? ROLE_OPTIONS : ROLE_OPTIONS.filter(r => !PRIVILEGED_ROLES.includes(r.value))),
    [isAdmin],
  );

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [roleCounts, setRoleCounts] = useState({});
  const [totalAllRoles, setTotalAllRoles] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(null);
  const [allStages, setAllStages] = useState([]);
  const [allTypes, setAllTypes] = useState([]);

  const [pane, setPane] = useState(null); // 'add' | user
  const [form, setForm] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [resetFor, setResetFor] = useState(null);
  const [resetPassword, setResetPassword] = useState('LeRoy@123');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, limit: 50,
        search: search || undefined,
        role: roleFilter || undefined,
      };
      const res = await api.get('/users', { params });
      setUsers(res.data.users);
      setTotal(res.data.total);
      setRoleCounts(res.data.role_counts || {});
      setTotalAllRoles(res.data.total_all_roles ?? res.data.total);
    } catch {
      toast.error('Failed to load users');
    }
    setLoading(false);
  }, [page, search, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    api.get('/admin/machines/config/production-stages')
      .then(res => setAllStages(res.data.stages || []))
      .catch(() => setAllStages([]));
    api.get('/admin/product-configs/types-list')
      .then(res => setAllTypes(res.data.product_types || []))
      .catch(() => setAllTypes([]));
  }, []);

  const setField = (name, value) => {
    setForm(p => ({ ...p, [name]: value }));
    if (VALIDATORS[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: validateField(name, value, VALIDATORS[name]) }));
    }
  };

  const openAdd = () => {
    setForm({ ...EMPTY, password: randomPassword() });
    setFieldErrors({});
    setShowPassword(true);
    setPane('add');
  };

  const openEdit = (user) => {
    setForm({
      full_name: user.full_name || '',
      username: user.username || '',
      password: '',
      role: user.role || '',
      secondary_roles: user.secondary_roles || [],
      mobile_number: user.mobile_number || '',
      email: user.email || '',
      linked_stage_ids: user.linked_stage_ids || [],
      linked_product_types: user.linked_product_types || [],
    });
    setFieldErrors({});
    setShowPassword(false);
    setPane(user);
  };

  const closePane = () => { setPane(null); setForm(EMPTY); setFieldErrors({}); };

  const save = async () => {
    const isAdd = pane === 'add';
    const rules = isAdd ? VALIDATORS : { ...VALIDATORS, password: [] };
    const errors = {};
    for (const [name, ruleset] of Object.entries(rules)) {
      const error = validateField(name, form[name], ruleset);
      if (error) errors[name] = error;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Fix the highlighted fields first');
      return;
    }

    setSubmitting(true);
    try {
      if (isAdd) {
        await api.post('/users', {
          ...form,
          secondary_roles: form.secondary_roles || [],
          linked_stage_ids: form.linked_stage_ids || [],
          linked_product_types: form.linked_product_types || [],
        });
        toast.success('User created');
      } else {
        const payload = {};
        if (form.full_name !== pane.full_name) payload.full_name = form.full_name;
        if (form.username !== pane.username) payload.username = form.username;
        if (form.mobile_number !== (pane.mobile_number || '')) payload.mobile_number = form.mobile_number;
        if (form.email !== (pane.email || '')) payload.email = form.email;
        if (form.role !== pane.role) payload.role = form.role;
        if (JSON.stringify(form.secondary_roles) !== JSON.stringify(pane.secondary_roles || [])) {
          payload.secondary_roles = form.secondary_roles;
        }
        if (JSON.stringify(form.linked_stage_ids) !== JSON.stringify(pane.linked_stage_ids || [])) {
          payload.linked_stage_ids = form.linked_stage_ids;
        }
        if (JSON.stringify(form.linked_product_types) !== JSON.stringify(pane.linked_product_types || [])) {
          payload.linked_product_types = form.linked_product_types;
        }
        if (Object.keys(payload).length === 0) {
          toast.info('No changes to save');
          setSubmitting(false);
          closePane();
          return;
        }
        await api.put(`/users/${pane.id}`, payload);
        toast.success('User updated');
      }
      closePane();
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : detail || 'Failed to save user');
    }
    setSubmitting(false);
  };

  const toggleStatus = async (userId) => {
    try {
      const res = await api.patch(`/users/${userId}/status`);
      toast.success(res.data.message);
      if (res.data.warning) toast.info(res.data.warning, { duration: 6000 });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change user status');
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`/users/${deleteFor.id}`);
      toast.success(res.data.message);
      if (res.data.warning) toast.info(res.data.warning, { duration: 6000 });
      setDeleteFor(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
    setDeleting(false);
  };

  const doResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    try {
      const res = await api.post(`/users/${resetFor.id}/reset-password`, { new_password: resetPassword });
      toast.success(res.data.message);
      setResetFor(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const toggleSecondaryRole = (role) => setForm(prev => ({
    ...prev,
    secondary_roles: prev.secondary_roles.includes(role)
      ? prev.secondary_roles.filter(r => r !== role)
      : [...prev.secondary_roles, role],
  }));

  // linked_stage_ids holds registry ids on machine accounts and bare names on
  // supervisors, so a stage is matched on either and shown by name
  const stageRefs = useCallback(
    (stage) => [stage.id, stage.stage_name || stage.name].filter(Boolean),
    [],
  );

  const stageLabel = useCallback((ref) => {
    const stage = allStages.find(s => s.id === ref);
    return stage ? (stage.stage_name || stage.name) : ref;
  }, [allStages]);

  const typeLabel = useCallback((code) => {
    const type = allTypes.find(t => t.code === code);
    return type ? (type.short_name || type.name || code) : code;
  }, [allTypes]);

  const toggleType = (code) => setForm(prev => ({
    ...prev,
    linked_product_types: prev.linked_product_types.includes(code)
      ? prev.linked_product_types.filter(c => c !== code)
      : [...prev.linked_product_types, code],
  }));

  const toggleStage = (stage) => {
    const refs = stageRefs(stage);
    setForm(prev => ({
      ...prev,
      linked_stage_ids: prev.linked_stage_ids.some(s => refs.includes(s))
        ? prev.linked_stage_ids.filter(s => !refs.includes(s))
        : [...prev.linked_stage_ids, stage.stage_name || stage.name],
    }));
  };

  const usernameChanged = pane && pane !== 'add' && form.username !== pane.username;

  const columns = useMemo(() => ([
    {
      key: 'full_name',
      header: 'User',
      width: 'minmax(12rem,1.3fr)',
      render: (u) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{u.full_name}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{u.username}</span>
        </>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      width: 'minmax(10rem,1fr)',
      render: (u) => (
        <span className="flex flex-wrap items-center gap-1">
          <span className="text-[12px] font-medium text-slate-700">{ROLE_LABEL[u.role] || u.role}</span>
          {(u.secondary_roles || []).map(r => (
            <span key={r} className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
              {ROLE_LABEL[r] || r}
            </span>
          ))}
        </span>
      ),
    },
    {
      key: 'linked_stage_ids',
      header: 'Covers stages',
      width: 'minmax(11rem,1.2fr)',
      render: (u) => u.linked_stage_ids?.length ? (
        <span className="flex flex-wrap gap-1">
          {u.linked_stage_ids.slice(0, 3).map(s => (
            <span key={s} className="rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] text-orange-700">
              {stageLabel(s)}
            </span>
          ))}
          {u.linked_stage_ids.length > 3 && (
            <span className="text-[10px] text-slate-400">+{u.linked_stage_ids.length - 3}</span>
          )}
        </span>
      ) : <span className="text-slate-400">—</span>,
    },
    {
      key: 'linked_product_types',
      header: 'Handles types',
      width: 'minmax(8rem,0.9fr)',
      render: (u) => u.linked_product_types?.length ? (
        <span className="flex flex-wrap gap-1">
          {u.linked_product_types.map(c => (
            <span key={c} className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700">
              {typeLabel(c)}
            </span>
          ))}
        </span>
      ) : <span className="text-[11px] text-slate-400">All types</span>,
    },
    {
      key: 'contact',
      header: 'Contact',
      width: 'minmax(9rem,1fr)',
      render: (u) => (
        <>
          <span className="block truncate text-[12px] text-slate-600">{u.mobile_number || '—'}</span>
          {u.email && <span className="block truncate text-[11px] leading-tight text-slate-400">{u.email}</span>}
        </>
      ),
    },
    {
      key: 'last_login',
      header: 'Last login',
      width: '9rem',
      render: (u) => (
        <span className="whitespace-nowrap text-[11px] text-slate-500">
          {u.last_login ? formatIST(u.last_login, { compact: true }) : 'never'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '5.5rem',
      render: (u) => {
        // the API writes `status`; reading `is_active` here showed every account
        // as active, deactivated ones included
        const inactive = (u.status || 'active') === 'inactive';
        return (
          <span className={`text-[12px] font-medium ${inactive ? 'text-slate-400' : 'text-emerald-600'}`}>
            {inactive ? 'inactive' : 'active'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: '7.5rem',
      align: 'right',
      render: (u) => {
        if (!canManageAccount(actor, u)) {
          return <span className="text-[11px] text-slate-300" title="Only an admin can manage this account">admin only</span>;
        }
        const inactive = (u.status || 'active') === 'inactive';
        return (
          <span className="flex items-center justify-end gap-0.5">
            <CellButton icon={Pencil} label="Edit user" iconOnly onClick={() => openEdit(u)} />
            <CellButton icon={KeyRound} label="Reset password" iconOnly
              onClick={() => { setResetFor(u); setResetPassword('LeRoy@123'); setShowResetPassword(false); }} />
            <CellButton
              icon={inactive ? ToggleLeft : ToggleRight}
              label={inactive ? 'Activate user' : 'Deactivate user'}
              iconOnly
              tone={inactive ? 'good' : 'danger'}
              onClick={() => toggleStatus(u.id)}
            />
            {/* deleting is the admin's alone, and nobody deletes the account
                they are signed in on */}
            {isAdmin && u.id !== actor?.id && (
              <CellButton icon={Trash2} label="Delete user" iconOnly tone="danger"
                onClick={() => setDeleteFor(u)} />
            )}
          </span>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [stageLabel, typeLabel, actor, isAdmin]);

  const totalPages = Math.ceil(total / 50);

  const deleteDialog = (
    <AlertDialog open={!!deleteFor} onOpenChange={(open) => { if (!open) setDeleteFor(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {deleteFor?.full_name || deleteFor?.username}?</AlertDialogTitle>
          <AlertDialogDescription>
            The account is gone from this list for good — this cannot be undone here.
            Any job still assigned to them goes back on the board, and the login name{' '}
            <span className="font-medium text-slate-700">{deleteFor?.username}</span> is free to use again.
            Their name stays on the orders and files they worked on.
            To switch somebody off for a while, deactivate them instead.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); doDelete(); }}
            disabled={deleting}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {deleting ? 'Deleting…' : 'Delete account'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // one card per account below lg; the form takes the whole screen while it is
  // open, and the delete confirmation is the same dialog either way
  if (isMobile) {
    if (pane) {
      return (
        <>
          <PhoneUserForm
            mode={pane} form={form} fieldErrors={fieldErrors} onField={setField} roleOptions={roleOptions}
            showPassword={showPassword} setShowPassword={setShowPassword}
            onGeneratePassword={() => { setField('password', randomPassword()); setShowPassword(true); }}
            onToggleSecondaryRole={toggleSecondaryRole}
            allStages={allStages} stageRefs={stageRefs} onToggleStage={toggleStage}
            allTypes={allTypes} onToggleType={toggleType}
            submitting={submitting} onSave={save} onCancel={closePane}
          />
          {deleteDialog}
        </>
      );
    }
    return (
      <>
        <PhoneUsersList
          users={users} total={total} totalAllRoles={totalAllRoles} roleCounts={roleCounts} loading={loading}
          page={page} totalPages={totalPages} onPage={setPage}
          onSearch={(text) => { setSearch(text); setPage(1); }}
          roleFilter={roleFilter} onRole={(role) => { setRoleFilter(role); setPage(1); }}
          actor={actor} isAdmin={isAdmin} stageLabel={stageLabel} typeLabel={typeLabel}
          onNew={openAdd} onEdit={openEdit}
          onReset={(u) => { setResetFor(u); setResetPassword('LeRoy@123'); setShowResetPassword(false); }}
          onToggle={(u) => toggleStatus(u.id)}
          onDelete={setDeleteFor}
          resetFor={resetFor} resetPassword={resetPassword} setResetPassword={setResetPassword}
          showResetPassword={showResetPassword} setShowResetPassword={setShowResetPassword}
          onGeneratePassword={() => { setResetPassword(randomPassword()); setShowResetPassword(true); }}
          onResetSubmit={doResetPassword} onResetClose={() => setResetFor(null)}
        />
        {deleteDialog}
      </>
    );
  }

  return (
    <Screen data-testid="users-page">
      <CommandBar
        title="Users"
        subtitle={`${total} account${total === 1 ? '' : 's'}`}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchUsers, disabled: loading },
          { key: 'add', label: 'Add User', icon: Plus, tone: 'accent', onClick: openAdd, testId: 'create-user-btn' },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Name, username…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: 'all', label: 'All Users', value: totalAllRoles, tone: 'blue', title: 'Show every account' },
          { key: 'admin', label: 'Admin', value: roleCounts.admin || 0 },
          { key: 'supervisor', label: 'Supervisor', value: roleCounts.supervisor || 0 },
          { key: 'designer', label: 'Designer', value: roleCounts.designer || 0 },
          { key: 'receptionist', label: 'Reception', value: roleCounts.receptionist || 0 },
        ]}
        // no role filter IS the All tab, so the strip always has one tab lit
        activeKey={roleFilter || 'all'}
        onSelect={(key) => { setRoleFilter(key === 'all' ? null : key); setPage(1); }}
      >
        <Select value={roleFilter || 'all'} onValueChange={(v) => { setRoleFilter(v === 'all' ? null : v); setPage(1); }}>
          <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="Any role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">Any role</SelectItem>
            {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </StatStrip>

      <Pane
        title="Users"
        aside={`${users.length} shown`}
        scroll={false}
        bodyClassName="flex flex-col"
        actions={totalPages > 1 && (
          <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
            {page} / {totalPages}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
          </span>
        )}
      >
        <DataGrid
          data-testid="users-table"
          columns={columns}
          rows={users}
          loading={loading}
          getRowId={(u) => u.id}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <UserCog className="h-8 w-8 opacity-50" />
              No users found
            </span>
          }
        />
      </Pane>

      <SidePane
        open={!!pane}
        onClose={closePane}
        title={pane === 'add' ? 'Add user' : `Edit ${pane?.full_name || 'user'}`}
        subtitle={pane === 'add' ? 'They can sign in as soon as this is saved' : pane?.username}
        width="27rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={closePane}>Cancel</Button>
            <Button size="sm" disabled={submitting} onClick={save} data-testid="submit-user-btn"
              className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              {submitting ? 'Saving…' : pane === 'add' ? 'Create user' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Full name *</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.full_name}
              onChange={e => setField('full_name', e.target.value)} />
            <FieldError error={fieldErrors.full_name} />
          </label>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Username *</span>
              <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.username}
                onChange={e => setField('username', e.target.value)} />
              <FieldError error={fieldErrors.username} />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Role *</span>
              <Select value={form.role} onValueChange={v => setField('role', v)}>
                <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"><SelectValue placeholder="Pick a role" /></SelectTrigger>
                <SelectContent>
                  {roleOptions.map(r => <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <FieldError error={fieldErrors.role} />
            </label>
          </div>

          {usernameChanged && (
            <p className="flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Signing in as <strong>{pane.username}</strong> will stop working. They will need to use{' '}
                <strong>{form.username || '—'}</strong> from their next sign-in.
              </span>
            </p>
          )}

          {pane === 'add' && (
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Password *</span>
              <span className="relative mt-0.5 flex gap-1">
                <span className="relative flex-1">
                  <Input
                    className="h-8 pr-8 text-sm"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setField('password', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700"
                  >
                    {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </span>
                <Button type="button" variant="outline" size="sm" className="h-8 shrink-0 px-2"
                  title="Generate a password that is easy to read out"
                  onClick={() => { setField('password', randomPassword()); setShowPassword(true); }}>
                  <Wand2 className="h-3.5 w-3.5" />
                </Button>
              </span>
              <FieldError error={fieldErrors.password} />
            </label>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Mobile</span>
              <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.mobile_number}
                onChange={e => setField('mobile_number', e.target.value)} />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Email</span>
              <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.email}
                onChange={e => setField('email', e.target.value)} />
              <FieldError error={fieldErrors.email} />
            </label>
          </div>

          <div>
            <span className="text-[11px] font-medium text-slate-600">
              Also works as
              {form.secondary_roles.length > 0 && (
                <span className="ml-1 text-slate-400">({form.secondary_roles.length})</span>
              )}
            </span>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {roleOptions.filter(r => r.value !== form.role).map(r => {
                const on = form.secondary_roles.includes(r.value);
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => toggleSecondaryRole(r.value)}
                    aria-pressed={on}
                    className={`rounded border px-1.5 py-0.5 text-[11px] transition-colors ${
                      on ? 'border-brand-blue bg-brand-blue text-white'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-brand-blue/50'
                    }`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          {(form.role === 'supervisor' || form.secondary_roles.includes('supervisor')) && (
            <div>
              <span className="text-[11px] font-medium text-slate-600">
                Covers which stages
                {form.linked_stage_ids.length > 0 && (
                  <span className="ml-1 text-slate-400">({form.linked_stage_ids.length} selected)</span>
                )}
              </span>
              <div className="mt-0.5 max-h-44 space-y-0.5 overflow-y-auto rounded border border-slate-200 bg-white p-1">
                {allStages.length === 0 && <p className="p-1 text-xs text-slate-400">No stages available</p>}
                {allStages.map(stage => {
                  const name = stage.stage_name || stage.name;
                  const refs = stageRefs(stage);
                  const on = form.linked_stage_ids.some(s => refs.includes(s));
                  return (
                    <button
                      key={stage.id || stage.stage_key || name}
                      type="button"
                      onClick={() => toggleStage(stage)}
                      aria-pressed={on}
                      className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-slate-50 ${on ? 'bg-blue-50' : ''}`}
                    >
                      <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                        on ? 'border-brand-blue bg-brand-blue' : 'border-slate-300'
                      }`}>
                        {on && <Check className="h-2.5 w-2.5 text-white" />}
                      </span>
                      <span className="flex-1 truncate text-[12px] text-slate-700">{name}</span>
                      {stage.category && (
                        <Badge variant="outline" className="shrink-0 px-1 py-0 text-[9px] text-slate-500">
                          {stage.category}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>

              <span className="mt-2 block text-[11px] font-medium text-slate-600">
                Handles which types
                {form.linked_product_types.length > 0 && (
                  <span className="ml-1 text-slate-400">({form.linked_product_types.length} selected)</span>
                )}
              </span>
              <p className="text-[10px] text-slate-400">
                Packing, loading and his board are limited to these. None selected means every type.
              </p>
              <div className="mt-0.5 flex flex-wrap gap-1">
                {allTypes.length === 0 && <p className="p-1 text-xs text-slate-400">No product types available</p>}
                {allTypes.map(type => {
                  const on = form.linked_product_types.includes(type.code);
                  return (
                    <button
                      key={type.code}
                      type="button"
                      onClick={() => toggleType(type.code)}
                      aria-pressed={on}
                      className={`rounded border px-1.5 py-0.5 text-[11px] transition-colors ${
                        on ? 'border-brand-blue bg-brand-blue text-white'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-brand-blue/50'
                      }`}
                    >
                      {type.short_name || type.name || type.code}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SidePane>

      <SidePane
        open={!!resetFor}
        onClose={() => setResetFor(null)}
        title={`Reset password — ${resetFor?.full_name || ''}`}
        subtitle={resetFor?.username}
        width="22rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setResetFor(null)}>Cancel</Button>
            <Button size="sm" onClick={doResetPassword} className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              Reset password
            </Button>
          </>
        }
      >
        <div className="space-y-2 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">New password</span>
            <span className="relative mt-0.5 flex gap-1">
              <span className="relative flex-1">
                <Input
                  className="h-8 pr-8 text-sm"
                  type={showResetPassword ? 'text' : 'password'}
                  value={resetPassword}
                  onChange={e => setResetPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword(v => !v)}
                  aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700"
                >
                  {showResetPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </span>
              <Button type="button" variant="outline" size="sm" className="h-8 shrink-0 px-2"
                title="Generate a password that is easy to read out"
                onClick={() => { setResetPassword(randomPassword()); setShowResetPassword(true); }}>
                <Wand2 className="h-3.5 w-3.5" />
              </Button>
            </span>
          </label>
          <p className="text-[11px] text-slate-500">
            Read it to them and ask them to change it after signing in.
          </p>
        </div>
      </SidePane>

      {deleteDialog}
    </Screen>
  );
}
